import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import { SectionContainer, Text } from 'pages/RecommendationV2/V2Common.styles';
import { useAppState } from 'context/AppState';
import { featureToggles } from 'config/featureToggles';
import {
  SectionWrapper,
  SectionTitle,
  SectionDescription,
  ContentPlaceholder,
  MetaRow,
  MetaChip,
  TableWrapper,
  SeedRateTable,
  MonthGroupHeaderCell,
  PeriodSubHeader,
  PeriodCell,
  LegendRow,
  LegendItem,
  LegendSwatch,
  MobileMonthGrid,
  MobileMonthCard,
  MobileMonthCardHeader,
  MobileMonthCardPeriods,
  MobileMonthPeriodCol,
  MobileMonthPeriodHeader,
  MobileMonthPeriodCell,
} from './BrazilSeedRateExplorer.styles';

// Styled components for the product tabs
const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 2px solid #f0f0f0;
  margin-bottom: 24px;
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

const TabButton = styled.button<{ readonly $active: boolean }>`
  padding: 12px 20px;
  background: none;
  border: none;
  border-bottom: 3px solid ${(props) => (props.$active ? '#0071cd' : 'transparent')};
  color: ${(props) => (props.$active ? '#0071cd' : '#595959')};
  font-weight: ${(props) => (props.$active ? '600' : '500')};
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #0071cd;
  }

  &:focus-visible {
    outline: 2px solid #0071cd;
    outline-offset: -2px;
  }
`;

export interface SeedRateCell {
  seedRate: string | null;
  color: 'green' | 'blue' | null;
}

export interface SeedRateEntry {
  brand: string;
  product: string;
  tpp: number | null;
  meso: string;
  season: string;
  ufs: string;
  months: Record<string, [SeedRateCell, SeedRateCell, SeedRateCell]>;
}

interface BrazilSeedRateExplorerProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly sectionRef?: any;
}

export const MONTHS_ORDER = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const EMPTY_CELLS: [SeedRateCell, SeedRateCell, SeedRateCell] = [
  { seedRate: null, color: null },
  { seedRate: null, color: null },
  { seedRate: null, color: null },
];

/** Extracts the numeric TPP value from strings like "TPP02" → 2. */
export function parseTppNumber(tppStr: string): number {
  return parseInt(tppStr.replace(/\D/g, ''), 10);
}

/** Returns only the months that have at least one non-null seed rate. */
export function getActiveMonths(entry: SeedRateEntry): string[] {
  return MONTHS_ORDER.filter((month) => {
    const cells = entry.months[month] ?? EMPTY_CELLS;
    return cells.some((c) => c.seedRate !== null);
  });
}

export interface PlantingPeriod {
  month: string;
  period: 1 | 2 | 3;
}

/**
 * Strictly parses a report planting date (YYYY-MM-DD or ISO 8601).
 * Returns null when the date is missing or invalid.
 */
export function parsePlantingDate(plantingDate?: string | null): moment.Moment | null {
  if (!plantingDate) {
    return null;
  }
  const parsed = moment(plantingDate, ['YYYY-MM-DD', moment.ISO_8601], true);
  return parsed.isValid() ? parsed : null;
}

/**
 * Determines which month + 10-day period (1: days 1-10, 2: 11-20, 3: 21-end)
 * a planting date falls into, so the matching table column can be highlighted.
 */
export function getPlantingPeriod(plantingDate?: moment.Moment | null): PlantingPeriod | null {
  if (!plantingDate) {
    return null;
  }
  const day = plantingDate.date();
  const period: 1 | 2 | 3 = day <= 10 ? 1 : day <= 20 ? 2 : 3;
  return { month: MONTHS_ORDER[plantingDate.month()], period };
}

/**
 * Formats a planting date for display in the active i18n language
 * (e.g. "05 Sep 2025" / "05 de set. de 2025").
 */
export function formatPlantingDate(plantingDate: moment.Moment | null, language: string): string {
  if (!plantingDate) {
    return '';
  }
  return new Intl.DateTimeFormat(language, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(plantingDate.toDate());
}

export function BrazilSeedRateExplorer({ sectionRef }: BrazilSeedRateExplorerProps): ReactElement {
  const { t, i18n } = useTranslation();
  const {
    apiData: { recommendationCropwise },
  } = useAppState();

  const [seedRateData, setSeedRateData] = useState<SeedRateEntry[]>([]);
  const [matchedEntries, setMatchedEntries] = useState<SeedRateEntry[]>([]);
  const [activeTabIdx, setActiveTabIdx] = useState<number>(0);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/brazil-seed-rate.json`)
      .then((res) => res.json())
      .then((data: SeedRateEntry[]) => setSeedRateData(data))
      .catch(() => setSeedRateData([]));
  }, []);

  useEffect(() => {
    if (!recommendationCropwise || seedRateData.length === 0) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const agronomicInputs: any =
      recommendationCropwise?.recommendations?.[0]?.multi_field?.config?.agronomicInputs;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fieldsSegment: Record<string, any> = agronomicInputs?.fieldsSegment ?? {};
    const firstFieldSegment = Object.values(fieldsSegment)[0] as
      | { TPP?: string; Meso?: string; Season?: string }
      | undefined;

    const brFilters: { tpps?: string[]; segments?: string[] } = agronomicInputs?.brFilters ?? {};

    const rawTpp: string = firstFieldSegment?.TPP ?? brFilters?.tpps?.[0] ?? '';
    const meso: string = firstFieldSegment?.Meso ?? brFilters?.segments?.[0] ?? '';
    const season: string = firstFieldSegment?.Season ?? '';
    const tppNumber = rawTpp ? parseTppNumber(rawTpp) : null;

    const products: { product_name?: string; commercial_name?: string }[] =
      recommendationCropwise?.recommendations?.[0]?.products ?? [];

    const productNames = products
      .filter((p) => p.product_name && p.product_name !== 'None')
      .map((p) => ({
        productName: (p.product_name ?? '').toLowerCase(),
        commercialName: (p.commercial_name ?? '').toLowerCase(),
      }));

    const matched = seedRateData.filter((entry) => {
      const entryProduct = entry.product.toLowerCase();
      const productMatches = productNames.some(
        ({ productName, commercialName }) =>
          entryProduct === productName || entryProduct === commercialName
      );
      const tppMatches = tppNumber !== null ? entry.tpp === tppNumber : true;
      const mesoMatches = meso
        ? entry.meso.split('/').map((s) => s.trim()).includes(meso)
        : true;
      const seasonMatches = season ? entry.season === season : true;
      return productMatches && tppMatches && mesoMatches && seasonMatches;
    });

    setMatchedEntries(matched);
    setActiveTabIdx(0); // Reset to the first product tab on data change
    setHasSearched(true);
  }, [recommendationCropwise, seedRateData]);

  const activeEntry = matchedEntries[activeTabIdx];
  const activeMonths = activeEntry ? getActiveMonths(activeEntry) : [];
  const parsedPlantingDate = parsePlantingDate(
    recommendationCropwise?.recommendations?.[0]?.multi_field?.config?.agronomicInputs?.plantingDate
  );
  const plantingPeriod = getPlantingPeriod(parsedPlantingDate);
  const formattedPlantingDate = formatPlantingDate(parsedPlantingDate, i18n?.language || 'en');

  const isPlantingPeriodColumn = (month: string, period: number): boolean =>
    featureToggles.plantingWindowHighlight &&
    plantingPeriod?.month === month &&
    plantingPeriod?.period === period;

  return (
    <div ref={sectionRef}>
      <SectionContainer>
        <SectionWrapper>
          <SectionTitle>{t('Planting Rate Explorer')}</SectionTitle>
          <SectionDescription>
            {t(
              'Explore planting rate recommendations by product month and environmental conditions for Brazil Corn'
            )}
          </SectionDescription>

          {hasSearched && matchedEntries.length === 0 && (
            <ContentPlaceholder>
              <Text>{t('No seed rate data found for the selected products')}</Text>
            </ContentPlaceholder>
          )}

          {matchedEntries.length > 0 && (
            <>
              {/* Product Tabs Navigation */}
              <TabsContainer role="tablist">
                {matchedEntries.map((entry, idx) => (
                  <TabButton
                    key={`${entry.brand}-${entry.product}-${idx}`}
                    $active={idx === activeTabIdx}
                    onClick={() => setActiveTabIdx(idx)}
                    role="tab"
                    aria-selected={idx === activeTabIdx}
                  >
                    {entry.product}
                  </TabButton>
                ))}
              </TabsContainer>

              {activeEntry && (
                <>
                  <MetaRow>
                    <MetaChip>{t('Brand')}: {activeEntry.brand}</MetaChip>
                    {activeEntry.tpp !== null && (
                      <MetaChip>{t('TPP')}: {activeEntry.tpp}</MetaChip>
                    )}
                    {activeEntry.meso && (
                      <MetaChip>{t('Meso')}: {activeEntry.meso}</MetaChip>
                    )}
                    {activeEntry.season && (
                      <MetaChip>{t('Season')}: {activeEntry.season}</MetaChip>
                    )}
                    {formattedPlantingDate && (
                      <MetaChip>{t('Planting Date')}: {formattedPlantingDate}</MetaChip>
                    )}
                  </MetaRow>

                  {/* Desktop: horizontal table with months as column groups */}
                  <TableWrapper>
                    <SeedRateTable>
                      <thead>
                        <tr>
                          {activeMonths.map((month) => (
                            <MonthGroupHeaderCell key={month} colSpan={3}>
                              {t(month)}
                            </MonthGroupHeaderCell>
                          ))}
                        </tr>
                        <tr>
                          {activeMonths.map((month) =>
                            [1, 2, 3].map((period) => (
                              <PeriodSubHeader
                                key={`${month}-${period}`}
                                $isPlantingPeriod={isPlantingPeriodColumn(month, period)}
                              >
                                {period}
                              </PeriodSubHeader>
                            ))
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {activeMonths.map((month) => {
                            const cells = activeEntry.months[month] ?? EMPTY_CELLS;
                            return cells.map((cell, idx) => (
                              <PeriodCell
                                key={`${month}-${idx}`}
                                $cellColor={cell.color}
                                $isPlantingPeriod={isPlantingPeriodColumn(month, idx + 1)}
                              >
                                {cell.seedRate ?? '—'}
                              </PeriodCell>
                            ));
                          })}
                        </tr>
                      </tbody>
                    </SeedRateTable>
                  </TableWrapper>

                  {/* Mobile: stacked month cards */}
                  <MobileMonthGrid>
                    {activeMonths.map((month) => {
                      const cells = activeEntry.months[month] ?? EMPTY_CELLS;
                      return (
                        <MobileMonthCard key={month}>
                          <MobileMonthCardHeader>{t(month)}</MobileMonthCardHeader>
                          <MobileMonthCardPeriods>
                            {cells.map((cell, idx) => (
                              <MobileMonthPeriodCol key={idx}>
                                <MobileMonthPeriodHeader
                                  $isPlantingPeriod={isPlantingPeriodColumn(month, idx + 1)}
                                >
                                  {idx + 1}
                                </MobileMonthPeriodHeader>
                                <MobileMonthPeriodCell
                                  $cellColor={cell.color}
                                  $isPlantingPeriod={isPlantingPeriodColumn(month, idx + 1)}
                                >
                                  {cell.seedRate ?? '—'}
                                </MobileMonthPeriodCell>
                              </MobileMonthPeriodCol>
                            ))}
                          </MobileMonthCardPeriods>
                        </MobileMonthCard>
                      );
                    })}
                  </MobileMonthGrid>
                </>
              )}

              <LegendRow>
                <LegendItem>
                  <LegendSwatch $dotColor="green" />
                  {`${t('Recommended')} (P)`}
                </LegendItem>
                <LegendItem>
                  <LegendSwatch $dotColor="blue" />
                  {`${t('Acceptable')} (NP)`}
                </LegendItem>
              </LegendRow>
            </>
          )}
        </SectionWrapper>
      </SectionContainer>
    </div>
  );
}

export default BrazilSeedRateExplorer;
